import { round } from '@/utils/mathUtils';

interface PriceCalculationInput {
  price: number;
  quantity: string;
  bonus: string;
  discount: string;
  specialDiscount: string;
  tax: string;
}

interface PriceCalculationResult {
  qty: number;
  totalQty: number;
  mainPrice: number;
  pharmacyDiscount: number;
  specialDiscount: number;
  totalTax: number;
  totalPrice: number;
  bonusValue: number;
  netPrice: number;
  netDiscount: number;
  avgPrice: number;
  TotalAfterDiscount: number;
  totalCustomerPrice: number;
}

/**
 * Calculate all pricing values based on input parameters
 *
 * @param input - All required inputs for price calculation
 * @returns All calculated price values
 */
export function calculatePricing(
  input: PriceCalculationInput,
): PriceCalculationResult {
  const { price, quantity, bonus, discount, specialDiscount, tax } = input;

  // Helper functions for safe parsing
  const safeParseInt = (value: string): number => {
    const parsed = parseInt(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  const safeParseFloat = (value: string): number => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Parse base quantity
  const qty = quantity === '0' || !quantity ? 1 : safeParseInt(quantity);

  // Calculate total quantity including bonus
  const totalQty = qty + safeParseInt(bonus);

  // Calculate main price after tax deduction
  const mainPrice = price - safeParseFloat(tax);

  const totalCustomerPrice = price * qty;

  // Calculate discount factors
  const pharmacyDiscount = (100 - safeParseFloat(discount)) / 100;
  const specialDiscountFactor = (100 - safeParseFloat(specialDiscount)) / 100;

  // Calculate total tax amount
  const totalTax = tax ? totalQty * safeParseFloat(tax) : 0;

  // Calculate total price before bonus value deduction
  const totalPrice =
    mainPrice * qty * pharmacyDiscount * specialDiscountFactor + totalTax;

  // Calculate the value of bonus items
  const bonusValue =
    bonus !== '0' && bonus !== '' && !!bonus
      ? (totalPrice / totalQty) * safeParseInt(bonus)
      : 0;

  // Calculate the net price per unit
  const netPrice = (totalPrice - bonusValue) / qty;

  // Calculate the net discount percentage
  const netDiscount =
    netPrice && price ? round((1 - netPrice / price || 0) * 100, 3) : 0;

  // Calculate the average price per item (including bonus)
  const avgPrice = totalQty > 0 ? totalPrice / totalQty : 0;

  // important to note that this is the total price after discount
  const TotalAfterDiscount = totalPrice;

  return {
    qty,
    totalQty,
    mainPrice,
    pharmacyDiscount,
    specialDiscount: specialDiscountFactor,
    totalTax,
    totalPrice,
    bonusValue,
    netPrice,
    netDiscount,
    avgPrice,
    TotalAfterDiscount,
    totalCustomerPrice,
  };
}
